<template>
  <div>
    <v-row no-gutters style="gap: 0.5rem" class="align-center; mb-4">
      <v-col cols="auto" class="d-flex align-center">
        <span>{{ $i18n.t(`table.rzi${currentMetadata.type}.fito.title`) }}</span>
      </v-col>
      <v-col cols="auto">
        <base-rounded-button
          icon="mdi-plus"
          size="x-small"
          color="success"
          @click="handleAssignGeometry('point', null, () => openManageDialog('fito', item.id))"
          :tooltip="$i18n.t(`table.rzi${currentMetadata.type}.fito.add`)"
        />
      </v-col>
    </v-row>
    <v-row no-gutters class="text-left">
      <v-col v-if="!fitos.length" cols="12">
        <span class="text-caption">{{ $i18n.t(`table.rzi${currentMetadata.type}.fito.empty`) }}</span>
      </v-col>
      <v-col v-else cols="12">
        <v-expansion-panels accordion focusable>
          <v-expansion-panel v-for="(fito, index) in computedFitos" :key="index">
            <v-expansion-panel-header color="d-flex">
              <div class="d-flex align-center" style="gap: 0.5rem; width: 100%">
                <span>
                  {{
                    fito.properties[currentMetadata.fito.attributes.photoDate]
                      ? $dayjs(fito.properties[currentMetadata.fito.attributes.photoDate]).format('L')
                      : ''
                  }}
                </span>
                <span>
                  <base-rounded-button
                    icon="mdi-plus"
                    :custom-size="25"
                    icon-size="17"
                    color="success"
                    @click.stop="openManageDialog('fitoLayers', fito.id)"
                    :tooltip="$i18n.t(`table.rzi${currentMetadata.type}.fito.addLayer`)"
                  />
                </span>
              </div>
              <div class="d-flex ml-auto mr-4" style="gap: 0.5rem">
                <span v-for="action in getActions(fito)" :key="action.name">
                  <v-tooltip bottom color="font">
                    <template #activator="{ on }">
                      <v-icon v-on="{ ...on, ...action.listeners }" @click.stop="action.trigger(fito)">{{
                        action.icon
                      }}</v-icon>
                    </template>
                    {{ action.tooltip }}
                  </v-tooltip>
                </span>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <rzi-fito-layers
                :ref="`fito-layers__${fito.id}`"
                :current-metadata="currentMetadata"
                :parent-fito-id="fito.id"
                @openManageDialog="openManageDialog"
                @openDeleteDialog="openDeleteDialog"
              />
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>
    <add-edit-feature-dialog
      v-if="isManageDialogLoaded"
      :is-visible.sync="isManageDialogVisible"
      v-bind="manageData"
      @done="handleManageDone"
    >
      <template #[`row-input-${currentMetadata.fito.attributes.altitude}-append`]="{ changeValueTrigger }">
        <elevation-trigger :geometry="elevationGeometry" @changeValue="changeValueTrigger" />
      </template>
    </add-edit-feature-dialog>
    <main-dialog
      :v-if="isDeleteDialogLoaded"
      :is-visible.sync="isDeleteDialogVisible"
      :loading="isDeleteDialogLoading"
      :title="manageData?.title ?? ''"
      :body="manageData?.body ?? ''"
      @agree="handleDelete"
      agree-text="dialog.agreeDelete"
    />
  </div>
</template>

<script>
import AddEditFeatureDialog from '@/components/AddEditFeatureDialog';
import RziFitoLayers from '@/components/RziFitoLayers';
import MainDialog from '@/components/MainDialog';
import ElevationTrigger from '@/components/ElevationTrigger';

import { drawing, utils } from '@/mixins/rziHelpers';
import { get, call } from 'vuex-pathify';

export default {
  name: 'RziFito',
  mixins: [drawing, utils],
  components: {
    AddEditFeatureDialog,
    RziFitoLayers,
    ElevationTrigger,
    MainDialog,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    currentMetadata: {
      type: Object,
      required: true,
    },
    fitos: {
      type: Array,
    },
    isDrawingOn: {
      type: Boolean,
    },
    stickyButtons: {
      type: Array,
    },
  },
  data: () => ({
    isDeleteDialogLoaded: false,
    isDeleteDialogVisible: false,
    isDeleteDialogLoading: false,
    isManageDialogVisible: false,
    isManageDialogLoaded: false,
    manageData: null,
  }),
  computed: {
    dataSources: get('layers/metadata'),
    computedFitos: {
      get() {
        return this.fitos;
      },
      set(value) {
        this.$emit('update:fitos', value);
      },
    },
    fitoRetrievingData() {
      return {
        dataSource: this.currentMetadata.fito.datasource,
        filters: {
          $EQUAL: {
            [`${this.currentMetadata.fito.datasource}.${this.currentMetadata.fito.attributes.year}`]: {
              value: this.item.id,
            },
          },
        },
      };
    },
    elevationGeometry() {
      return this.manageData?.featureMetadata?.geometry ?? this.drawingGeometry ?? null;
    },
  },
  methods: {
    editNonTopoFeatureAction: call('layers/editDatasourceFeature'),
    getActions(item) {
      return [
        {
          name: 'zoom',
          icon: 'mdi-magnify',
          tooltip: this.$i18n.t('table.rziMonitoring.fito.zoomToFito'),
          trigger: this.zoomToFito,
          listeners: {
            mouseenter: () => this.handleFeatureHover(item.geometry),
            mouseleave: () => this.handleFeatureHover(null),
          },
        },
        {
          name: 'edit',
          icon: 'mdi-pencil',
          tooltip: this.$i18n.t('table.rziMonitoring.fito.editFito'),
          trigger: this.editFito,
        },
        {
          name: 'delete',
          icon: 'mdi-delete',
          tooltip: this.$i18n.t('table.rziMonitoring.fito.deleteFito'),
          trigger: data => this.openDeleteDialog('fito', data),
        },
      ];
    },
    async handleManageDone(closeDialog, featureData) {
      if (featureData.datasource === this.currentMetadata.fito.datasource) {
        this.handleCancelGeometry();
        try {
          const r = await this.getDataSourceFeatures(this.fitoRetrievingData);
          this.refreshLayers(
            [
              {
                name: this.currentMetadata.fito.datasource,
              },
            ],
            () => {}
          );
          this.computedFitos = r.features;
        } catch (error) {
          console.error(error);
        }
      } else {
        const fitoId = this.manageData.staticProperties[this.currentMetadata.fitoLayers.attributes.fito];
        await this.$refs[`fito-layers__${fitoId}`]?.[0]?.init();
      }
      closeDialog();
    },
    zoomToFito(item) {
      this.zoomToFeature(item.geometry);
    },
    editFito(item) {
      this.openManageDialog('fito', this.item.id, item);
    },
    openDeleteDialog(type, item) {
      const customTypeData = {
        fito: {
          title: this.$i18n.t('table.rziMonitoring.fito.deleteFito'),
          body: this.$i18n.t('table.rziMonitoring.fito.deleteFitoBody'),
        },
        fitoLayers: {
          title: this.$i18n.t('table.rziMonitoring.fito.deleteLayer'),
          body: this.$i18n.t('table.rziMonitoring.fito.deleteLayerBody'),
          staticProperties: {
            [this.currentMetadata.fitoLayers.attributes.fito]:
              item.properties[this.currentMetadata.fitoLayers.attributes.fito],
          },
        },
      };
      this.manageData = {
        ...customTypeData[type],
        datasource: this.currentMetadata[type].datasource,
        id: item.id,
      };
      this.isDeleteDialogLoaded = this.isDeleteDialogVisible = true;
    },
    async handleDelete() {
      this.isDeleteDialogLoading = true;
      try {
        await this.deleteDatasourceFeature({
          datasource: this.manageData.datasource,
          id: this.manageData.id,
          params: {
            cascade: true,
          },
        });
        await this.handleManageDone(() => (this.isDeleteDialogVisible = false), {
          datasource: this.manageData.datasource,
        });
      } catch (error) {
        console.error(error);
      } finally {
        this.isDeleteDialogLoading = false;
      }
    },
    openManageDialog(type, foreignKey, item) {
      const customTypeData = {
        fito: {
          hiddenAttributes: [this.currentMetadata.fito.attributes.year],
          staticProperties: {
            ...(!item &&
              this.drawingGeometry && {
                [this.dataSources[this.currentMetadata[type].datasource]?.attributes_schema.geometry_name]: this
                  .drawingGeometry,
              }),
            [this.currentMetadata.fito.attributes.year]: foreignKey,
          },
        },
        fitoLayers: {
          hiddenAttributes: [this.currentMetadata.fitoLayers.attributes.fito],
          staticProperties: {
            [this.currentMetadata.fitoLayers.attributes.fito]: foreignKey,
          },
        },
      };
      this.manageData = {
        featureData: {
          ...(item && { id: item.id }),
          datasource: this.currentMetadata[type].datasource,
        },
        ...(item && { featureMetadata: item }),
        hiddenAttributes: [
          '_systems',
          ...(item ? [] : this.currentMetadata[type].hiddenAttributes ?? []),
          ...(customTypeData[type].hiddenAttributes ?? []),
        ],
        staticProperties: {
          ...(customTypeData[type].staticProperties ?? {}),
        },
      };
      this.isManageDialogLoaded = this.isManageDialogVisible = true;
    },
  },
};
</script>
